import { Alert, Button, Form, message, Select, Spin, Tag } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { OrderFormSkeleton } from '@/components/skeletons/OrderFormSkeleton';
import { AppModal } from '@/components/ui/AppModal';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import { isFinalOrderStatus } from '@/core/utils/is_final_order_status';
import { canShowRecallTransition, requiresDriver } from '@/core/utils/order_status_validation';
import { OrderComments } from '../../components/OrderComments';
import { OrderPositions } from '../../components/OrderPositions';
import { OrderChangesView } from '../../OrderChanges/OrderChangesView';
import {
  useEditOrderPositions,
  useGetMeta,
  useGetOrderByIdWithDriversWarehouses,
  useGetPaymentTypes,
  useInsertUserViewsLog,
  useUpdateOrderFields,
} from '../../query/orders.hooks';
import { AddressSection } from './components/AddressSection';
import { OrderDetailsSection } from './components/OrderDetailsSection';
import { OrderDrawerFormPicturesBlock } from './components/OrderDrawerFormPicturesBlock';
import { OrderHeader } from './components/OrderHeader';
import { OrderStatusSection } from './components/OrderStatusSection';
import { layoutFormProps } from './layout';

interface OrderDrawerFormProps {
  orderId: number;
  close: () => void;
}

interface Position {
  __typename?: 'order_position';
  id: number;
  quantity: number;
  productID: string;
  totalPrice?: number | null;
  product: { __typename?: 'product'; id: string; name: string };
}

export const OrderDrawerForm: React.FC<OrderDrawerFormProps> = ({ orderId, close }) => {
  const { userDataParams } = useAuth();
  const [showChangesHistoryModal, setShowChangesHistoryModal] = useState(false);
  const [positions, setPositions] = useState<Position[]>([]);
  const [form] = useForm();

  const { selectedDepartmentId: departmentId, departments, selectedDepartment } = useDepartments();
  const updateOrderFields = useUpdateOrderFields();
  const editOrderPositions = useEditOrderPositions();
  const insertUserViewsLog = useInsertUserViewsLog();

  const {
    data: orderData,
    isLoading: isOrderLoading,
    isFetching,
    refetch,
  } = useGetOrderByIdWithDriversWarehouses({
    departmentId,
    id: orderId,
  });

  const { data: driversData } = useGetMeta({ departmentId });

  const [orderChanges, setOrderChanges] = useState<Record<string, unknown>>({});
  const [addressChanges, setAddressChanges] = useState<Record<string, unknown>>({});
  const [clientChanges, setClientChanges] = useState<Record<string, unknown>>({});
  const [originalPositions, setOriginalPositions] = useState<Position[]>([]);

  const { data: paymentTypesData } = useGetPaymentTypes();

  const shouldRefetchOrder = useCallback(() => {
    return orderData?.order_by_pk && orderData.order_by_pk.id !== orderId;
  }, [orderData, orderId]);

  const updatePositionsFromOrderData = useCallback(() => {
    if (!orderData?.order_by_pk?.positions || orderData.order_by_pk.id !== orderId) return;
    const positionsAsPositions = orderData.order_by_pk.positions as Position[];
    setPositions(positionsAsPositions);
    setOriginalPositions(positionsAsPositions);
  }, [orderData, orderId]);

  useEffect(() => {
    if (shouldRefetchOrder()) {
      refetch();
    }
  }, [shouldRefetchOrder, refetch]);

  useEffect(() => {
    updatePositionsFromOrderData();
  }, [updatePositionsFromOrderData]);

  const getMergedDriver = useCallback(
    (
      originalOrder:
        | NonNullable<
            ReturnType<typeof useGetOrderByIdWithDriversWarehouses>['data']
          >['order_by_pk']
        | null
        | undefined
    ) => {
      if (!originalOrder) return null;

      const pendingId = orderChanges.driverID;
      if (pendingId === undefined || pendingId === null || pendingId === '') {
        return originalOrder.driver;
      }

      const pendingStr = String(pendingId);
      const originalIdStr = originalOrder.driver?.id != null ? String(originalOrder.driver.id) : '';

      const fromMeta = driversData?.user.find((u) => String(u.id) === pendingStr);
      if (fromMeta) return fromMeta;

      if (pendingStr !== originalIdStr) {
        return {
          ...(originalOrder.driver ?? {}),
          id: pendingStr,
          name: originalOrder.driver?.name ?? '',
          email: originalOrder.driver?.email ?? '',
          blocked: false,
        };
      }

      return originalOrder.driver;
    },
    [orderChanges.driverID, driversData]
  );

  const getMergedStatus = useCallback(
    (
      originalOrder:
        | NonNullable<
            ReturnType<typeof useGetOrderByIdWithDriversWarehouses>['data']
          >['order_by_pk']
        | null
        | undefined
    ) => {
      if (!originalOrder) return null;
      if (!orderChanges.statusName) return originalOrder.status;
      return {
        ...originalOrder.status,
        name: orderChanges.statusName,
        status_transitions: originalOrder.status?.status_transitions || [],
      };
    },
    [orderChanges.statusName]
  );

  const getMergedClient = useCallback(
    (
      originalOrder:
        | NonNullable<
            ReturnType<typeof useGetOrderByIdWithDriversWarehouses>['data']
          >['order_by_pk']
        | null
        | undefined
    ) => {
      if (!originalOrder?.client) return null;
      return {
        ...originalOrder.client,
        ...clientChanges,
        address: originalOrder.client.address
          ? {
              ...originalOrder.client.address,
              ...addressChanges,
            }
          : null,
      };
    },
    [addressChanges, clientChanges]
  );

  const mergedOrderData = React.useMemo(() => {
    if (!orderData?.order_by_pk) return null;

    const originalOrder = orderData.order_by_pk;
    if (!originalOrder) return null;

    return {
      ...originalOrder,
      ...orderChanges,
      driver: getMergedDriver(originalOrder),
      status: getMergedStatus(originalOrder),
      client: getMergedClient(originalOrder),
    } as typeof originalOrder;
  }, [orderData, orderChanges, getMergedDriver, getMergedStatus, getMergedClient]);

  const loading = React.useMemo(
    () =>
      isOrderLoading || isFetching || updateOrderFields.isPending || editOrderPositions.isPending,
    [isOrderLoading, isFetching, updateOrderFields.isPending, editOrderPositions.isPending]
  );

  const userPermissions = React.useMemo(
    () => ({
      isManagerOrSeniorOperator: Boolean(
        userDataParams?.roles.includes('manager') ||
          userDataParams?.roles.includes('senior-operator')
      ),
      isManager: Boolean(userDataParams?.roles.includes('manager')),
      isUserIdExists: userDataParams?.sub !== null && userDataParams?.sub !== undefined,
    }),
    [userDataParams?.roles, userDataParams?.sub]
  );

  const isOrderIdExists = React.useMemo(
    () => Boolean(orderData?.order_by_pk?.id),
    [orderData?.order_by_pk?.id]
  );

  const { isManagerOrSeniorOperator, isManager, isUserIdExists } = userPermissions;

  const initialStatusName = orderData?.order_by_pk?.status?.name;
  const isEditingLocked = useMemo(() => isFinalOrderStatus(initialStatusName), [initialStatusName]);

  const isDriverBlocked = Boolean(mergedOrderData?.driver?.blocked);
  const originalDriverId = orderData?.order_by_pk?.driver?.id;
  const hasDriverChanged =
    orderChanges.driverID != null &&
    orderChanges.driverID !== '' &&
    String(orderChanges.driverID) !== String(originalDriverId ?? '');

  const hasStatusChanged =
    orderChanges.statusName !== undefined && orderChanges.statusName !== initialStatusName;

  const isRecallAvailable =
    isDriverBlocked &&
    !hasDriverChanged &&
    !hasStatusChanged &&
    canShowRecallTransition(initialStatusName);

  const newStatusName = orderChanges.statusName as string | undefined;
  const isTransitioningToFinalStatus = useMemo(
    () => Boolean(newStatusName && isFinalOrderStatus(newStatusName)),
    [newStatusName]
  );

  const handleOrderFieldChange = (field: string, value: string | number) => {
    if (isEditingLocked) return;
    setOrderChanges((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: string, value: string | number | null) => {
    if (isEditingLocked) return;
    setAddressChanges((prev) => ({ ...prev, [field]: value }));
  };

  const handleClientChange = (field: string, value: string | number) => {
    if (isEditingLocked) return;
    setClientChanges((prev) => ({ ...prev, [field]: value }));
  };

  const handleDriverChange = (driverId: string) => {
    handleOrderFieldChange('driverID', driverId);
  };

  const handleStatusChange = (statusName: string) => {
    handleOrderFieldChange('statusName', statusName);
  };

  const handleRevertStatus = () => {
    setOrderChanges((prev) => {
      const { statusName: _, ...rest } = prev;
      return rest;
    });
  };

  const handleReasonChange = (reason: string) => {
    handleOrderFieldChange('reasonRefusal', reason);
  };

  const handleInsertUserViewsLog = () => {
    if (isOrderIdExists && isUserIdExists) {
      const userId = userDataParams?.userId ?? '';
      insertUserViewsLog.mutate({ userId, orderId: orderData?.order_by_pk?.id });
    }
  };

  const handleDepartmentChange = (departmentId: string | number) => {
    handleOrderFieldChange(
      'departmentId',
      typeof departmentId === 'string' ? parseInt(departmentId, 10) : departmentId
    );
  };

  const handleOrderDetailsChange = (field: string, value: string | number) => {
    const orderFields = ['plannedDate', 'plannedDateDuration'];

    if (orderFields.includes(field)) {
      handleOrderFieldChange(field, value);
    } else {
      handleClientChange(field, value);
    }
  };

  const hasPositionsChanged = () => {
    if (originalPositions.length !== positions.length) {
      return true;
    }

    const buildSignature = (list: Position[]) => {
      const map = new Map<string, number[]>();
      for (const pos of list) {
        const arr = map.get(pos.productID) ?? [];
        arr.push(pos.quantity);
        map.set(pos.productID, arr);
      }
      const entries = Array.from(map.entries()).map(([productId, quantities]) => [
        productId,
        quantities.sort((a, b) => a - b),
      ]);
      entries.sort((a, b) => (a[0] as string).localeCompare(b[0] as string));
      return JSON.stringify(entries);
    };

    const originalSig = buildSignature(originalPositions);
    const currentSig = buildSignature(positions);
    return originalSig !== currentSig;
  };

  const positionsChanged = hasPositionsChanged();
  const hasChanges =
    Object.keys(orderChanges).length > 0 ||
    Object.keys(addressChanges).length > 0 ||
    Object.keys(clientChanges).length > 0 ||
    positionsChanged;

  const buildUpdatesObject = useCallback(() => {
    const updates: Record<string, unknown> = {};

    if (Object.keys(orderChanges).length > 0) {
      updates.orderUpdates = orderChanges;
    }

    if (Object.keys(addressChanges).length > 0) {
      updates.addressUpdates = addressChanges;
      updates.addressId = orderData?.order_by_pk?.client?.address?.id;
    }

    if (Object.keys(clientChanges).length > 0) {
      updates.clientUpdates = clientChanges;
      updates.clientId = orderData?.order_by_pk?.client?.id;
    }

    return updates;
  }, [orderChanges, addressChanges, clientChanges, orderData]);

  const hasOtherChanges = useMemo(() => {
    return (
      Object.keys(orderChanges).length > 0 ||
      Object.keys(addressChanges).length > 0 ||
      Object.keys(clientChanges).length > 0
    );
  }, [orderChanges, addressChanges, clientChanges]);

  const resetChanges = useCallback(() => {
    setOrderChanges({});
    setAddressChanges({});
    setClientChanges({});
    setOriginalPositions([...positions]);
  }, [positions]);

  const updateOrderFieldsData = useCallback(async () => {
    if (!orderData?.order_by_pk) return;

    const updates = buildUpdatesObject();
    await updateOrderFields.mutateAsync({
      orderId: orderData.order_by_pk.id,
      ...updates,
    });
  }, [orderData, buildUpdatesObject, updateOrderFields]);

  const updateOrderPositionsData = useCallback(async () => {
    if (!orderData?.order_by_pk) return;

    await editOrderPositions.mutateAsync({
      orderID: orderData.order_by_pk.id,
      umbrellaID: orderData.order_by_pk.umbrellaID ?? '',
      orderItems: positions.map((position) => ({
        productId: position.productID,
        qty: position.quantity,
      })),
    });
  }, [orderData, positions, editOrderPositions]);

  const handleSubmit = async () => {
    if (!orderData?.order_by_pk || !hasChanges) return;

    if (orderData.order_by_pk.id !== orderId) {
      console.error('Order data mismatch: trying to save wrong order');
      return;
    }

    if (isEditingLocked) {
      console.warn('Cannot save changes for order in final status');
      return;
    }

    if (newStatusName && requiresDriver(newStatusName)) {
      const currentDriver = getMergedDriver(orderData.order_by_pk);

      if (!currentDriver?.id) {
        message.error(
          'Cannot set order to this status without assigning a driver. Please select a driver first.'
        );
        return;
      }
    }

    try {
      if (hasOtherChanges) {
        await updateOrderFieldsData();
      }

      if (positionsChanged) {
        await updateOrderPositionsData();
      }

      resetChanges();
    } catch (error) {
      console.error('Failed to update order:', error);
    }
  };

  const isDataMismatch = orderData?.order_by_pk && orderData.order_by_pk.id !== orderId;

  if (isDataMismatch || !orderData?.order_by_pk || !mergedOrderData) {
    if (isDataMismatch) {
      console.warn(
        `Order data mismatch: loaded order ${orderData?.order_by_pk?.id}, expected ${orderId}`
      );
    }
    return <OrderFormSkeleton />;
  }

  const order = mergedOrderData;

  return (
    <Spin spinning={loading}>
      <AppModal
        open={showChangesHistoryModal}
        onCancel={() => setShowChangesHistoryModal(false)}
        footer={null}
        destroyOnHidden={true}
      >
        <OrderChangesView orderId={orderId} />
      </AppModal>

      <Form {...layoutFormProps} form={form} size="small" onFinish={handleSubmit}>
        <OrderHeader
          orderData={order}
          onShowHistory={() => setShowChangesHistoryModal(true)}
          onClose={close}
          hasChanges={hasChanges}
          isLoading={loading}
          isDisabled={isEditingLocked}
        />

        {isTransitioningToFinalStatus && (
          <Alert
            title="Final status selected"
            description="This status is final. After saving, the order will become read-only and cannot be moved to another status."
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
            action={
              <Button size="small" type="primary" onClick={handleRevertStatus}>
                Revert
              </Button>
            }
          />
        )}

        {isEditingLocked && (
          <Alert
            title="Editing is disabled"
            description="This order is in a final status and cannot be edited. All fields are read-only."
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {isDriverBlocked && !hasDriverChanged && (
          <Alert
            title="Driver is blocked"
            description={
              isRecallAvailable
                ? `Driver "${orderData?.order_by_pk?.driver?.name}" is blocked. You can transfer the order to Recall status or assign a new driver.`
                : `Driver "${orderData?.order_by_pk?.driver?.name}" is blocked. Please assign a new driver.`
            }
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {order.resale && (
          <Tag color="gold" style={{ fontSize: '14px', marginBottom: '8px' }}>
            Reventa
          </Tag>
        )}

        {order.isSaved && (
          <Tag color="red" style={{ fontSize: '14px', marginBottom: '8px' }}>
            Recuperado
          </Tag>
        )}

        <OrderStatusSection
          orderData={order}
          driversData={driversData}
          onDriverChange={handleDriverChange}
          onStatusChange={handleStatusChange}
          onReasonChange={handleReasonChange}
          isDisabled={isEditingLocked}
          isDriverBlocked={isDriverBlocked}
          hasDriverChanged={hasDriverChanged}
          hasStatusChanged={hasStatusChanged}
          initialStatusName={initialStatusName}
        />

        <OrderDetailsSection
          orderData={order}
          selectedDepartment={selectedDepartment as any}
          onFieldChange={handleOrderDetailsChange}
          onInsertUserViewsLog={handleInsertUserViewsLog}
          isOrderIdExists={isOrderIdExists}
          isUserIdExists={isUserIdExists}
          currentPlannedDate={orderChanges.plannedDate as string | undefined}
          currentPlannedDateDuration={orderChanges.plannedDateDuration as number | undefined}
          currentFullname={clientChanges.fullname as string | undefined}
          currentPhone={clientChanges.phone as string | undefined}
          isDisabled={isEditingLocked}
        />

        <AddressSection
          orderData={order}
          onAddressChange={handleAddressChange}
          currentStreet={addressChanges.street as string | undefined}
          currentComments={addressChanges.comments as string | undefined}
          isDisabled={isEditingLocked}
        />

        {isManager && (
          <FormItem className="app-form-item" label="Department">
            <Select
              size="small"
              value={
                departments.find((department) => department.id === order.departmentId)?.name ||
                'No departments'
              }
              style={{ width: 150 }}
              onChange={handleDepartmentChange}
              showSearch
              optionFilterProp="children"
              disabled={isEditingLocked}
            >
              {departments.map((department) => (
                <Select.Option key={department.id} value={department.id}>
                  {department.name}
                </Select.Option>
              ))}
            </Select>
          </FormItem>
        )}

        {order.delivery_provider && (
          <FormItem className="app-form-item" label="Provider">
            <div>{order.delivery_provider.display_name}</div>
          </FormItem>
        )}

        <FormItem className="app-form-item" label="Payment Type">
          <Select
            size="small"
            style={{ width: 200 }}
            value={order?.paymentType || ''}
            onChange={(value) => {
              handleOrderFieldChange('paymentType', value);
            }}
            placeholder="Select Payment Type"
            disabled={isEditingLocked}
          >
            {paymentTypesData?.payment_type.map((pt) => (
              <Select.Option key={pt.value} value={pt.value}>
                {pt.value
                  .split('_')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                  .join(' ')}
              </Select.Option>
            ))}
          </Select>
        </FormItem>

        <OrderPositions
          isManagerOrSeniorOperator={isManagerOrSeniorOperator}
          positions={positions}
          setPositions={setPositions}
          orderForm={orderData as any}
          isDisabled={isEditingLocked}
        />

        {order.reasonRefusal && (
          <p>
            <b>Reason: {order.reasonRefusal}</b>
          </p>
        )}

        <OrderDrawerFormPicturesBlock pictures={order.order_photos || []} />
      </Form>

      <OrderComments orderId={orderId} />
    </Spin>
  );
};
